/**
 * Single source of truth for every color derived from the tenant accent.
 *
 * The SPA's runtime injector derives its palette here. The static token
 * defaults in `ui/src/styles.css` are pinned to the same output for the
 * default accent by `ui-brand-palette.vitest.js`.
 */

import { DEFAULT_BRAND_ACCENT, resolveAccentContrast } from "./branding.js";

export const BRAND_SOFT_ALPHA = 0.1;
export const BRAND_SOFT_2_ALPHA = 0.18;
export const BRAND_RING_ALPHA = 0.3;

export interface BrandPalette {
  brand: string;
  /** Hover/active shade — the accent at 85% of its HSL lightness (−15% L). */
  brandDark: string;
  brandSoft: string;
  brandSoft2: string;
  brandRing: string;
  /** WCAG-picked on-accent text color (white or ink). */
  brandContrast: string;
}

function hexChannels(hex: string): [number, number, number] {
  const value = hex.replace(/^#/, "");
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

export function rgbaFromHex(hex: string, alpha: number): string {
  const [r, g, b] = hexChannels(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * The documented "−15% lightness" hover shade (packages/hub/docs/design/tokens.md):
 * convert to HSL, scale L by 0.85, convert back. HSL rather than per-channel
 * RGB offsets because a uniform RGB offset clips low channels to 0 and shifts
 * hue/saturation on saturated accents — the old injector produced #ae5200
 * for the default amber where this derivation (and the link page's static
 * copy) says #b46616.
 */
export function deriveBrandDark(hex: string): string {
  const [r8, g8, b8] = hexChannels(hex);
  const r = r8 / 255;
  const g = g8 / 255;
  const b = b8 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = l < 0.5 ? d / (max + min) : d / (2 - max - min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
    else if (max === g) h = ((b - r) / d + 2) * 60;
    else h = ((r - g) / d + 4) * 60;
  }
  return hslToHex(h, s, l * 0.85);
}

function hslToHex(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb: [number, number, number];
  if (h < 60) rgb = [c, x, 0];
  else if (h < 120) rgb = [x, c, 0];
  else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c];
  else if (h < 300) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  return `#${rgb
    .map((channel) => {
      const v = Math.round((channel + m) * 255);
      return Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
    })
    .join("")}`;
}

export function deriveBrandPalette(accentHex: string): BrandPalette {
  return {
    brand: accentHex,
    brandDark: deriveBrandDark(accentHex),
    brandSoft: rgbaFromHex(accentHex, BRAND_SOFT_ALPHA),
    brandSoft2: rgbaFromHex(accentHex, BRAND_SOFT_2_ALPHA),
    brandRing: rgbaFromHex(accentHex, BRAND_RING_ALPHA),
    brandContrast: resolveAccentContrast(accentHex),
  };
}

/** The palette every static (non-injected) surface must mirror. */
export const DEFAULT_BRAND_PALETTE: BrandPalette = deriveBrandPalette(DEFAULT_BRAND_ACCENT);
